import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Rocket, CheckCircle2, XCircle, RefreshCcw, RotateCcw, Undo2, ExternalLink, ArrowRight, ArrowLeft, Sparkles, Loader2, Package, FileText } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PIPELINE_STEPS = [
  "tenant", "subscription", "billing", "license",
  "environment", "configuration", "provisioning",
  "deployment", "deployment_runner", "infrastructure",
  "payment_validation", "domain", "dns_apply",
  "dns_verify", "ssl_issue", "auto_binding",
  "website_refresh", "monitoring_refresh", "scheduler_refresh",
];

const STATUS_TONE = {
  NEW: "slate", VALIDATING: "cyan", FIXING_BLOCKERS: "amber",
  PROVISIONING: "cyan", DEPLOYING: "indigo", DOMAIN: "sky",
  DNS: "sky", SSL: "purple", BINDING: "violet", VERIFYING: "cyan",
  ONLINE: "emerald", FAILED: "rose", ROLLING_BACK: "amber", ROLLED_BACK: "slate",
};

function StatusBadge({ status }) {
  const T = STATUS_TONE[status] || "slate";
  const cls = {
    slate: "bg-white/10 text-white/70 border-white/20",
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
    sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    purple: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  }[T];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${cls}`} data-testid="ocl-status-badge">{status}</span>;
}

function ProgressBar({ launch }) {
  const results = launch.step_results || {};
  const done = PIPELINE_STEPS.filter(s => results[s]?.ok).length;
  const pct = Math.round((done / PIPELINE_STEPS.length) * 100);
  const isFailed = launch.status === "FAILED";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="ocl-progress">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-xs font-black uppercase tracking-widest text-white/70">Pipeline Progress</div>
        <StatusBadge status={launch.status} />
        <div className="ml-auto text-white font-mono text-sm">{done}/{PIPELINE_STEPS.length} steps · {pct}%</div>
      </div>
      <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full transition-all duration-500 ${isFailed ? "bg-gradient-to-r from-rose-500 to-red-500" : "bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="ocl-steps">
        {PIPELINE_STEPS.map((s, i) => {
          const r = results[s];
          const active = launch.current_step === s;
          const ok = r?.ok;
          const bg = ok ? "border-emerald-500/30 bg-emerald-500/10" : active ? "border-cyan-500/40 bg-cyan-500/10 animate-pulse" : r ? "border-rose-500/30 bg-rose-500/10" : "border-white/10 bg-white/5";
          const Icon = ok ? CheckCircle2 : (r ? XCircle : Loader2);
          const iconCls = ok ? "text-emerald-400" : (r ? "text-rose-400" : "text-white/40");
          return (
            <div key={s} className={`rounded-lg border p-2 text-xs ${bg}`} data-testid={`ocl-step-${s}`}>
              <div className="flex items-center gap-2">
                <Icon size={12} className={`${iconCls} ${active ? "animate-spin" : ""}`} />
                <span className="font-mono text-white/80 truncate">{i + 1}. {s.replace(/_/g, " ")}</span>
              </div>
              {r?.message && <div className="mt-1 text-[10px] text-white/50 font-mono truncate">{r.message}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RootCauseCard({ launch }) {
  const rc = launch?.root_cause;
  if (!rc) return null;
  return (
    <div
      className="rounded-2xl border border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-amber-500/5 p-4 mb-3"
      data-testid="ocl-root-cause-card"
    >
      <div className="flex items-start gap-3">
        <XCircle className="text-rose-300 shrink-0 mt-0.5" size={20} />
        <div className="flex-1 min-w-0">
          <div className="text-rose-200 font-black text-sm uppercase tracking-wider">
            Root Cause · {rc.step} · {rc.phase}
          </div>
          {rc.provision_error_message && (
            <div className="mt-2 rounded-lg bg-black/40 border border-rose-500/20 p-3 text-xs font-mono text-rose-100 break-all" data-testid="ocl-root-cause-message">
              {rc.provision_error_message}
            </div>
          )}
          {rc.error && (
            <div className="mt-2 rounded-lg bg-black/40 border border-rose-500/20 p-3 text-xs font-mono text-rose-100 break-all" data-testid="ocl-root-cause-error">
              <span className="text-amber-300">{rc.error_class}:</span> {rc.error}
            </div>
          )}
          {Array.isArray(rc.failed_verification_checks) && rc.failed_verification_checks.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid="ocl-root-cause-checks">
              <span className="text-[10px] text-white/50 uppercase mr-1 self-center">Failed checks:</span>
              {rc.failed_verification_checks.map((c) => (
                <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border border-rose-500/30">
                  {c}
                </span>
              ))}
            </div>
          )}
          {rc.rollback_steps > 0 && (
            <div className="mt-2 text-[11px] text-white/60">
              Rollback: <strong className="text-amber-300">{rc.rollback_steps} step(s)</strong> di-eksekusi.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Timeline({ launch }) {
  const entries = launch.timeline || [];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="ocl-timeline">
      <div className="text-xs font-black uppercase tracking-widest text-white/70 mb-3">Immutable Timeline ({entries.length})</div>
      <div className="max-h-64 overflow-auto space-y-1.5">
        {entries.map((e, i) => (
          <div key={i} className="flex gap-2 text-xs font-mono" data-testid={`ocl-timeline-entry-${i}`}>
            <span className="text-white/40 shrink-0">{e.at?.slice(11, 19)}</span>
            <span className={e.ok ? "text-emerald-400" : "text-rose-400"}>{e.ok ? "✓" : "✗"}</span>
            <span className="text-white/70">{e.step}</span>
            <span className="text-white/50 truncate">{e.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultPanel({ result, onReset }) {
  const nav = useNavigate();
  const url = result.website_url;
  const admin = result.admin_url;
  return (
    <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 p-6" data-testid="ocl-result-panel">
      <div className="flex items-center gap-3 mb-4">
        <Rocket size={32} className="text-emerald-300" />
        <div>
          <div className="text-emerald-200 font-black text-2xl">Website Online</div>
          <div className="text-xs text-emerald-100/70">Launch complete in {result.duration_seconds?.toFixed(1)}s</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-black mb-1">Tenant</div>
          <div className="text-white font-mono">{result.tenant?.tenant_name} · {result.tenant?.tenant_slug}</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-black mb-1">Launch Number</div>
          <div className="text-white font-mono">{result.launch_number}</div>
        </div>
        {url && (
          <div className="rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-black mb-1">Website URL</div>
            <div className="text-cyan-300 font-mono truncate">{url}</div>
          </div>
        )}
        {admin && (
          <div className="rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-black mb-1">Admin URL</div>
            <div className="text-cyan-300 font-mono truncate">{admin}</div>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {url && <a href={url} target="_blank" rel="noopener noreferrer"><Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="ocl-open-website"><ExternalLink size={14} className="mr-1" /> Open Website</Button></a>}
        {admin && <a href={admin} target="_blank" rel="noopener noreferrer"><Button className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="ocl-open-admin"><ExternalLink size={14} className="mr-1" /> Open Admin</Button></a>}
        <Button variant="ghost" onClick={() => nav(`/platform/monitoring/${result.tenant_id}`)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-open-monitoring">Monitoring</Button>
        <Button variant="ghost" onClick={() => nav(`/platform/billing/${result.tenant_id}`)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-open-billing">Billing</Button>
        <Button variant="ghost" onClick={onReset} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-new-launch">
          <Sparkles size={14} className="mr-1" /> New Launch
        </Button>
      </div>
    </div>
  );
}

function Wizard({ onLaunch, busy }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_slug: "",
    plan: "STARTER",
    billing_cycle: "MONTHLY",
    template_key: "grocery",
    owner_name: "",
    owner_email: "",
    domain: "",
    subdomain: "",
    dns_provider: "MANUAL",
    ssl_provider: "LETS_ENCRYPT",
    payment_method: "MANUAL",
    license_days: 30,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const domainAuto = useMemo(() => form.domain || (form.tenant_slug ? `${form.tenant_slug}.dynicty.com` : ""), [form.domain, form.tenant_slug]);
  const canNext1 = form.tenant_name && form.tenant_slug;
  const canNext2 = domainAuto;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="ocl-wizard">
      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3, 4].map(n => (
          <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-gradient-to-r from-cyan-500 to-purple-500" : "bg-white/10"}`} />
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-black mb-3">Step {step} of 4</div>

      {step === 1 && (
        <div className="space-y-3" data-testid="ocl-wiz-step-1">
          <div className="text-white font-display font-black text-lg mb-2">Tenant · Plan · Template</div>
          <input value={form.tenant_name} onChange={e => set("tenant_name", e.target.value)} placeholder="Tenant Name" className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-input-tenant-name" />
          <input value={form.tenant_slug} onChange={e => set("tenant_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="Tenant Slug (e.g. toko-mawar)" className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm font-mono" data-testid="ocl-input-tenant-slug" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.plan} onChange={e => set("plan", e.target.value)} className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-plan">
              <option value="STARTER">STARTER</option>
              <option value="GROWTH">GROWTH</option>
              <option value="ENTERPRISE">ENTERPRISE</option>
            </select>
            <select value={form.billing_cycle} onChange={e => set("billing_cycle", e.target.value)} className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-billing-cycle">
              <option value="MONTHLY">MONTHLY</option>
              <option value="SIX_MONTHS">SIX_MONTHS</option>
              <option value="YEARLY">YEARLY</option>
              <option value="LIFETIME">LIFETIME</option>
            </select>
          </div>
          <select value={form.template_key} onChange={e => set("template_key", e.target.value)} className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-template">
            <option value="grocery">grocery</option>
            <option value="fashion">fashion</option>
            <option value="food">food</option>
            <option value="electronics">electronics</option>
            <option value="beauty">beauty</option>
            <option value="general">general</option>
          </select>
          <input value={form.owner_name} onChange={e => set("owner_name", e.target.value)} placeholder="Owner Name (optional)" className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-input-owner-name" />
          <input value={form.owner_email} onChange={e => set("owner_email", e.target.value)} placeholder="Owner Email (optional)" className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-input-owner-email" />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="ocl-wiz-step-2">
          <div className="text-white font-display font-black text-lg mb-2">Domain · DNS · SSL</div>
          <input value={form.domain} onChange={e => set("domain", e.target.value.toLowerCase())} placeholder={`Domain (default: ${form.tenant_slug}.dynicty.com)`} className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm font-mono" data-testid="ocl-input-domain" />
          <input value={form.subdomain} onChange={e => set("subdomain", e.target.value.toLowerCase())} placeholder="Subdomain (optional)" className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm font-mono" data-testid="ocl-input-subdomain" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.dns_provider} onChange={e => set("dns_provider", e.target.value)} className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-dns">
              <option value="MANUAL">MANUAL</option>
              <option value="CLOUDFLARE">CLOUDFLARE</option>
              <option value="HOSTINGER">HOSTINGER (stub)</option>
              <option value="CPANEL">CPANEL (stub)</option>
            </select>
            <select value={form.ssl_provider} onChange={e => set("ssl_provider", e.target.value)} className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-ssl">
              <option value="LETS_ENCRYPT">LETS_ENCRYPT</option>
              <option value="EXISTING">EXISTING</option>
              <option value="CUSTOM">CUSTOM</option>
            </select>
          </div>
          <select value={form.payment_method} onChange={e => set("payment_method", e.target.value)} className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ocl-select-payment">
            <option value="MANUAL">MANUAL Transfer</option>
            <option value="MIDTRANS">MIDTRANS</option>
          </select>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-2" data-testid="ocl-wiz-step-3">
          <div className="text-white font-display font-black text-lg mb-2">Summary</div>
          {Object.entries({ ...form, domain: domainAuto }).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs border-b border-white/5 py-1.5">
              <span className="text-white/50 font-mono uppercase">{k.replace(/_/g, " ")}</span>
              <span className="text-white font-mono">{String(v || "—")}</span>
            </div>
          ))}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3" data-testid="ocl-wiz-step-4">
          <div className="text-white font-display font-black text-lg mb-2">Ready To Launch</div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="text-emerald-200 text-sm">Kami akan menjalankan 19 step pipeline otomatis: tenant → subscription → billing → license → environment → configuration → provisioning → deployment → runner → infrastructure → payment → domain → dns_apply → dns_verify → ssl → auto_binding → website_refresh → monitoring → scheduler.</div>
            <div className="text-emerald-100/60 text-xs mt-2">Estimasi durasi: 2-5 detik (dry-run/mock).</div>
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        {step > 1 && <Button variant="ghost" onClick={() => setStep(s => s - 1)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-btn-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>}
        <div className="ml-auto flex gap-2">
          {step < 3 && (
            <Button onClick={() => setStep(s => s + 1)} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)} className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black" data-testid="ocl-btn-next">
              Next <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={() => setStep(4)} className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black" data-testid="ocl-btn-review">
              Review <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {step === 4 && (
            <Button onClick={() => onLaunch({ ...form, domain: domainAuto })} disabled={busy} className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:opacity-90 text-white font-black" data-testid="ocl-btn-launch">
              {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Launching…</> : <><Rocket size={14} className="mr-1" /> Create & Go Live</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function TenantPrefillPanel({ tenant, template, goLive, onStartLaunch, onCancel, busy }) {
  return (
    <div className="rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 via-purple-500/5 to-emerald-500/10 p-6" data-testid="ocl-prefill-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500 grid place-items-center shrink-0">
          <Rocket size={26} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-display font-black text-xl">Launch Website</div>
          <div className="text-white/60 text-sm">
            Auto-loaded dari <strong className="text-cyan-300">{tenant.tenant_name}</strong> · Tekan Start Launch untuk menjalankan 19-step pipeline.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-white/10 bg-black/25 p-4" data-testid="ocl-prefill-tenant">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-black flex items-center gap-1 mb-1">
            <Package size={11} /> Tenant
          </div>
          <div className="text-white font-mono text-sm truncate">{tenant.tenant_name}</div>
          <div className="text-white/50 text-xs font-mono truncate">{tenant.tenant_slug} · {tenant.tenant_code}</div>
          <div className="text-white/40 text-[10px] uppercase mt-1">Status: <span className="text-cyan-300 font-black">{tenant.tenant_status}</span></div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4" data-testid="ocl-prefill-template">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-black flex items-center gap-1 mb-1">
            <Sparkles size={11} /> Template
          </div>
          {template ? (
            <>
              <div className="text-white font-mono text-sm truncate">{template.name}</div>
              <div className="text-white/50 text-xs font-mono truncate">{template.slug} · v{template.version}</div>
              <div className="text-white/40 text-[10px] uppercase mt-1">Category: {template.category}</div>
            </>
          ) : (
            <div className="text-white/40 text-xs italic">Template default (grocery)</div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-black/25 p-4" data-testid="ocl-prefill-golive">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-black flex items-center gap-1 mb-1">
            <FileText size={11} /> Go Live Checklist
          </div>
          {goLive ? (
            <>
              <div className="text-white font-mono text-sm">
                {goLive.total_ready ?? goLive.production_ready_count ?? 0} / {goLive.total ?? "?"} ready
              </div>
              <div className="text-white/50 text-xs">
                {goLive.production_ready ? (
                  <span className="text-emerald-300 font-black">✓ Production Ready</span>
                ) : (
                  <span className="text-amber-300">Belum semua siap — Launch tetap bisa dijalankan</span>
                )}
              </div>
            </>
          ) : (
            <div className="text-white/40 text-xs italic">Belum di-evaluate</div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={onStartLaunch}
          disabled={busy}
          className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:opacity-90 text-white font-black text-base px-6 py-5 shadow-lg shadow-emerald-500/20"
          data-testid="ocl-btn-start-launch"
        >
          {busy ? (
            <><Loader2 size={16} className="mr-2 animate-spin" /> Launching…</>
          ) : (
            <><Rocket size={16} className="mr-2" /> Start Launch</>
          )}
        </Button>
        <Button
          onClick={onCancel}
          variant="ghost"
          className="text-white/60 hover:text-white hover:bg-white/10"
          data-testid="ocl-btn-cancel-prefill"
        >
          Batal
        </Button>
      </div>
    </div>
  );
}

export default function LaunchCenter() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillTenantId = searchParams.get("tenant_id");

  const [launch, setLaunch] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  // Prefill state
  const [prefillTenant, setPrefillTenant] = useState(null);
  const [prefillTemplate, setPrefillTemplate] = useState(null);
  const [prefillGoLive, setPrefillGoLive] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    try { const r = await api.get("/platform/one-click-launch/history", { params: { limit: 20 } }); setHistory(r.data); }
    catch (e) { notifyError(e); }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Auto-load prefill when tenant_id is in URL
  useEffect(() => {
    if (!prefillTenantId) {
      setPrefillTenant(null);
      setPrefillTemplate(null);
      setPrefillGoLive(null);
      return;
    }
    (async () => {
      setPrefillLoading(true);
      try {
        const tRes = await api.get(`/platform/tenants/${prefillTenantId}`);
        setPrefillTenant(tRes.data);
        // Template (best-effort)
        const tplKey = tRes.data.template_key || tRes.data.template_id;
        if (tplKey) {
          try {
            const tplRes = await api.get(`/platform/template-registry/${tplKey}`);
            setPrefillTemplate(tplRes.data);
          } catch { /* ignore */ }
        }
        // Go Live checklist
        try {
          const glRes = await api.get(`/platform/go-live/${prefillTenantId}`);
          setPrefillGoLive(glRes.data);
        } catch { /* ignore */ }
      } catch (e) {
        notifyError(e, "Gagal memuat tenant untuk launch");
      } finally {
        setPrefillLoading(false);
      }
    })();
  }, [prefillTenantId]);

  const doLaunch = useCallback(async (payload) => {
    setBusy(true);
    try {
      const r = await api.post("/platform/one-click-launch", payload);
      setLaunch(r.data);
      if (r.data.status === "ONLINE") {
        toast.success(`🚀 Website ONLINE (${r.data.duration_seconds?.toFixed(1)}s)`);
        const res = await api.get(`/platform/one-click-launch/${r.data.id}/result`);
        setResult(res.data);
        // Auto-redirect ke Tenant Detail dengan banner (jika tenant_id ada)
        const targetTenantId = r.data.tenant_id || payload.tenant_id;
        if (targetTenantId) {
          setTimeout(() => {
            navigate(`/platform/tenants/${targetTenantId}?launched=1`);
          }, 1500);
        }
      } else if (r.data.status === "FAILED") {
        toast.error(`Launch FAILED at ${r.data.current_step}`);
      }
      await loadHistory();
    } catch (e) { notifyError(e); }
    finally { setBusy(false); }
  }, [loadHistory, navigate]);

  const onLaunch = useCallback((payload) => doLaunch(payload), [doLaunch]);

  const onStartPrefillLaunch = useCallback(() => {
    if (!prefillTenant) return;
    const t = prefillTenant;
    doLaunch({
      tenant_id: t.tenant_id,
      tenant_name: t.tenant_name,
      tenant_slug: t.tenant_slug,
      plan: t.package || "STARTER",
      billing_cycle: "MONTHLY",
      template_key: t.template_key || "grocery",
      owner_name: t.owner_name || "",
      owner_email: t.owner_email || "",
      domain: `${t.tenant_slug}.dynicty.com`,
      dns_provider: "MANUAL",
      ssl_provider: "LETS_ENCRYPT",
      payment_method: "MANUAL",
    });
  }, [prefillTenant, doLaunch]);

  const onRetry = async () => {
    if (!launch) return;
    setBusy(true);
    try {
      const r = await api.post(`/platform/one-click-launch/${launch.id}/retry`);
      setLaunch(r.data);
      if (r.data.status === "ONLINE") {
        const res = await api.get(`/platform/one-click-launch/${r.data.id}/result`);
        setResult(res.data);
        toast.success("Retry berhasil");
      }
    } catch (e) { notifyError(e); }
    finally { setBusy(false); }
  };

  const onRollback = async () => {
    if (!launch) return;
    if (!window.confirm("Rollback launch ini? Immutable timeline dipertahankan.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/platform/one-click-launch/${launch.id}/rollback`);
      setLaunch(r.data);
      setResult(null);
      toast.success("Rollback selesai");
    } catch (e) { notifyError(e); }
    finally { setBusy(false); }
  };

  const reset = () => { setLaunch(null); setResult(null); loadHistory(); };

  return (
    <PlatformLayout title="Launch Center">
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-white font-display font-black text-xl">One Click Launch</div>
          <div className="text-white/50 text-xs">Master Orchestrator — 19 step pipeline dari Tenant → Website ONLINE.</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {history && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-black">Total: <span className="text-white">{history.summary.total_launches}</span></div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Online: {history.summary.online}</div>
              <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">Failed: {history.summary.failed}</div>
            </>
          )}
          <Button variant="ghost" onClick={loadHistory} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-refresh-history"><RefreshCcw size={12} className="mr-1" /> Refresh</Button>
        </div>
      </div>

      {result ? (
        <div className="space-y-4">
          <ResultPanel result={result} onReset={reset} />
          {launch && <ProgressBar launch={launch} />}
          {launch && <Timeline launch={launch} />}
        </div>
      ) : launch ? (
        <div className="space-y-4">
          <RootCauseCard launch={launch} />
          <ProgressBar launch={launch} />
          <Timeline launch={launch} />
          <div className="flex gap-2">
            {launch.status === "FAILED" && <Button onClick={onRetry} disabled={busy} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="ocl-btn-retry"><RotateCcw size={14} className="mr-1" /> Retry from {launch.current_step}</Button>}
            {launch.status === "ONLINE" && <Button onClick={onRollback} disabled={busy} variant="ghost" className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10" data-testid="ocl-btn-rollback"><Undo2 size={14} className="mr-1" /> Rollback</Button>}
            <Button onClick={reset} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ocl-btn-reset">Start New</Button>
          </div>
        </div>
      ) : prefillTenantId ? (
        prefillLoading ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="ocl-prefill-loading">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
            <div className="text-white/60 text-sm mt-3">Memuat tenant dari URL…</div>
          </div>
        ) : prefillTenant ? (
          <TenantPrefillPanel
            tenant={prefillTenant}
            template={prefillTemplate}
            goLive={prefillGoLive}
            onStartLaunch={onStartPrefillLaunch}
            onCancel={() => navigate(`/platform/tenants/${prefillTenantId}`)}
            busy={busy}
          />
        ) : (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-6" data-testid="ocl-prefill-not-found">
            <div className="text-rose-200 font-black">Tenant tidak ditemukan</div>
            <div className="text-rose-100/70 text-sm mt-1">tenant_id={prefillTenantId} tidak ada di registry.</div>
            <Button onClick={() => navigate("/platform/launch")} className="mt-3 bg-white/10 hover:bg-white/20 text-white">Buka Launch Wizard</Button>
          </div>
        )
      ) : (
        <Wizard onLaunch={onLaunch} busy={busy} />
      )}

      {history && history.items?.length > 0 && !launch && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="ocl-history-table">
          <div className="px-4 py-3 border-b border-white/10 text-xs font-black uppercase tracking-widest text-white/70">Recent Launches</div>
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/50">
                <th className="px-4 py-2 font-bold">Launch#</th>
                <th className="px-4 py-2 font-bold">Tenant</th>
                <th className="px-4 py-2 font-bold">Status</th>
                <th className="px-4 py-2 font-bold">Duration</th>
                <th className="px-4 py-2 font-bold">Started</th>
              </tr>
            </thead>
            <tbody>
              {history.items.slice(0, 10).map(it => (
                <tr key={it.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setLaunch(it)} data-testid={`ocl-history-row-${it.id}`}>
                  <td className="px-4 py-2 font-mono text-white/80 text-xs">{it.launch_number}</td>
                  <td className="px-4 py-2 text-white/80 text-xs">{it.wizard?.tenant_name || it.tenant_id?.slice(0, 8)}</td>
                  <td className="px-4 py-2"><StatusBadge status={it.status} /></td>
                  <td className="px-4 py-2 text-white/60 text-xs font-mono">{it.duration_seconds ? it.duration_seconds.toFixed(1) + "s" : "—"}</td>
                  <td className="px-4 py-2 text-white/50 text-xs font-mono">{it.started_at?.slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PlatformLayout>
  );
}
